import { ConfigFactory } from './ConfigFactory';
import { PushConfig } from './push/PushConfig';
import { RuntimeConfig } from './RuntimeConfig';
import { StartupConfig } from './StartupConfig';
import { ThemingConfig } from './ThemingConfig';
import { Constants } from '../common/Constants';
import * as InternalTypes from '../common/InternalTypes';
import { Logger } from '../common/Logger';
import { LoggerTexts } from '../common/LoggerTexts';
import { ScopeCheck } from '../common/ScopeCheck';
import { ConfigIdentifier, TenantInfo } from '../common/Types';
import { Util } from '../common/Util';

export class CentralConfig {
	private readonly _logger: Logger;
	private _version: string;
	private readonly _configIdentifier: ConfigIdentifier;
	private readonly _tenantInfo: TenantInfo;
	private _startupConfig: StartupConfig;
	private _runtimeConfig: RuntimeConfig;
	private _pushConfig?: PushConfig;
	private _themingConfig?: ThemingConfig;

	constructor(configIdentifier: ConfigIdentifier, tenantInfo: TenantInfo) {
		this._logger = new Logger(Constants.FILE_CENTRAL_CFG);
		if (!configIdentifier) {
			throw new Error(LoggerTexts.CENTRAL_CFG.CTOR_CFG_ID_CANNOT_BE_NULL);
		}
		if (!tenantInfo) {
			throw new Error(LoggerTexts.CENTRAL_CFG.CTOR_TENANT_INFO_CANNOT_BE_NULL);
		}
		this._configIdentifier = configIdentifier;
		this._tenantInfo = tenantInfo;
	}

	public initWithConfig(config: InternalTypes.PxConfig) {
		const { startupConfig, runtimeConfig, pushConfig, themingConfig } = config;
		this._version = config.version;
		this._startupConfig = ConfigFactory.createStartupConfig(startupConfig);
		this._runtimeConfig = ConfigFactory.createRuntimeConfig(runtimeConfig);
		if (ScopeCheck.isAnyPushFeatureActive(startupConfig)) {
			this._pushConfig = ConfigFactory.createPushConfig(startupConfig, pushConfig);
		}

		if (themingConfig && !Util.isObjectEmpty(themingConfig)) {
			this._themingConfig = ConfigFactory.createThemingConfig(themingConfig);
		} else {
			this._logger.info(LoggerTexts.CENTRAL_CFG.CTOR_THEMING_CFG_EMPTY);
		}
	}

	get version(): string {
		return this._version;
	}

	get configIdentifier(): ConfigIdentifier {
		return this._configIdentifier;
	}

	get tenantInfo(): TenantInfo {
		return this._tenantInfo;
	}

	get startupConfig(): StartupConfig {
		return this._startupConfig;
	}

	get runtimeConfig(): RuntimeConfig {
		return this._runtimeConfig;
	}

	set pushConfig(pushConfig: PushConfig) {
		this._pushConfig = pushConfig;
	}

	get pushConfig(): PushConfig | undefined {
		return this._pushConfig;
	}

	get themingConfig(): ThemingConfig | undefined {
		return this._themingConfig;
	}
}
